import { PREVIEW_REFERENCE } from "@/features/material-compliance/components/supplier-declarations/inquiry/settings/templateSettingsMock"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"

interface DeclarationPdfPreviewProps {
  logoUrl: string | null
  companyName: string
  address: string
  contactPerson: string
  sendingAddress: string | null
  /** The email signature, printed as written: placeholders too. */
  signOff: string
  /** The PDF footer the PDF-only form prints at the bottom; without it, the
   *  company name and sending address (PDF + Excel). */
  footer?: string
}

/** One "label value" line of the PDF's sender block; "–" for an empty value,
 *  as the PDF leaves it blank. */
function SenderField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-3">
      <span className="w-16 shrink-0 text-[#888]">{label}</span>
      <span className="min-w-0 whitespace-pre-line text-[#222]">
        {value.trim() || "–"}
      </span>
    </div>
  )
}

/**
 * Preview of the declaration page of the inquiry PDF (declaration_v2): the
 * letterhead and sender block, the email signature as its sign-off, and at the
 * bottom the PDF footer (PDF-only form) or the company name with the sending
 * address (PDF + Excel).
 */
export function DeclarationPdfPreview({
  logoUrl,
  companyName,
  address,
  contactPerson,
  sendingAddress,
  signOff,
  footer,
}: DeclarationPdfPreviewProps) {
  const { translate } = useTranslation()
  const signOffLines = signOff
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
  const footerLine = [companyName.trim(), sendingAddress ?? ""]
    .filter(Boolean)
    .join(" · ")

  return (
    <div className="rounded-lg bg-white px-6 py-5 text-[12.5px] leading-relaxed text-[#222] shadow-lg">
      <div className="flex items-start justify-between gap-4 border-b border-[#ececec] pb-4">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={translate("MC_TPL_LOGO_ALT")}
            className="h-6 max-w-[160px] object-contain"
          />
        ) : (
          <span className="h-6 w-24 rounded bg-[#ededed]" aria-hidden />
        )}
        <div className="font-mono text-right text-[10.5px] leading-[1.7] text-[#888]">
          {translate("MC_TPL_PREVIEW_REF_PREFIX", { ref: PREVIEW_REFERENCE })}
        </div>
      </div>

      <div className="py-4 text-[15px] font-semibold text-[#111]">
        {translate("SETTINGS/PREVIEW/DECLARATION_TITLE")}
      </div>

      <div className="flex flex-col gap-1 text-[11px]">
        <SenderField
          label={translate("SETTINGS/PREVIEW/ADDRESS")}
          value={address}
        />
        <SenderField
          label={translate("SETTINGS/PREVIEW/CONTACT")}
          value={contactPerson}
        />
        <SenderField
          label={translate("SETTINGS/PREVIEW/EMAIL")}
          value={sendingAddress ?? ""}
        />
      </div>

      {/* The request and how to respond: the same for every org. */}
      <div className="mt-4 flex flex-col gap-1.5" aria-hidden>
        <span className="block h-2 w-full rounded bg-[#ededed]" />
        <span className="block h-2 w-11/12 rounded bg-[#ededed]" />
        <span className="block h-2 w-3/4 rounded bg-[#ededed]" />
      </div>

      {signOffLines.length > 0 && (
        <div className="mt-4 text-[11.5px] text-[#444]">
          {signOffLines.map((line, index) => (
            <p key={index} className={index === 0 ? "mb-2" : undefined}>
              {line}
            </p>
          ))}
        </div>
      )}

      <div className="mt-5 border-t border-[#ececec] pt-3 text-[10.5px] leading-[1.6] whitespace-pre-line text-[#999]">
        {footer ?? footerLine}
      </div>
    </div>
  )
}
